import { Box, Typography } from '@mui/material';
import {
  DefaultSchoolYear,
  FilterKeys,
  FilterSection,
  GetPayableFeesArrangeBy,
  GetPayableFeesQueryFilters,
  PAYABLE_FEES_ARRANGE_BY_FILTER_KEYS,
  PAYABLE_FEES_QUERY_FILTER_KEYS,
  SchoolYear,
} from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import {
  AgeGroupExpandedSelect,
  ArrangeByDropdown,
  DateRangeDropdown,
  DayPastDueExpandedSelect,
  DayPastDueTagSelect,
  FeeStatusExpandedSelect,
  FeeStatusTagSelect,
  FilterDropdown,
  FiltersContainer,
  FiltersSidebarMethods,
  FrequencyExpandedSelect,
  FrequencyTagSelect,
  PersonalFiltersDropdown,
  pickOnlyParamsFromFilterKeys,
  ProductExpandedSelect,
  ProductTagSelect,
  ProductTypeTagSelect,
  renderPropertyGroupTags,
} from '@schooly/components/filters';
import { SchoolPropertyType, SchoolUserRole } from '@schooly/constants';
import { forwardRef, useCallback, useImperativeHandle, useRef } from 'react';

import { PayableFeesFiltersSidebar } from './PayableFeesFiltersSidebar';
import { usePayableFeesFilters } from './usePayableFeesFilters';

type PayableFeesFiltersProps = {
  arrangeBy: GetPayableFeesArrangeBy;
  onSetArrangeBy: (v: GetPayableFeesArrangeBy) => void;
  schoolId: string;
  filters: GetPayableFeesQueryFilters;
  defaultFilters: GetPayableFeesQueryFilters;
  onSetFilters: (v: GetPayableFeesQueryFilters) => void;
  defaultSchoolYear?: DefaultSchoolYear;
  defaultUserFilters: GetPayableFeesQueryFilters;
  schoolYears: SchoolYear[];
};

export type PayableFeesFilters = {
  openArrangeBy: () => void;
};

export const PayableFeesFilters = forwardRef<PayableFeesFilters, PayableFeesFiltersProps>(
  (
    {
      defaultFilters,
      filters,
      schoolId,
      onSetFilters,
      defaultSchoolYear,
      defaultUserFilters,
      arrangeBy,
      onSetArrangeBy,
      schoolYears,
    },
    ref,
  ) => {
    const sidebar = useRef<FiltersSidebarMethods | null>(null);
    const personalFiltersDropdown = useRef<PersonalFiltersDropdown | null>(null);
    const { currentStaff } = useAuth();
    const arrangeByDropdown = useRef<ArrangeByDropdown | null>(null);

    const {
      labels,
      schoolYear,
      selectedItemsForAgeGroups,
      filtersDate,
      filtersProduct,
      filtersProductTypes,
      filtersFrequencies,
      filtersFeeStatus,
      filtersDayPastDue,
      filtersAgeGroup,
      getSchoolLevelById,
      getAgeGroupById,
      getAgeGroupsByLevelId,
      onSetDate,
      onSetProductAndProductType,
      onClearProduct,
      onSetFrequency,
      onClearFrequency,
      onSetFeeStatus,
      onClearFeeStatus,
      onSetDayPastDue,
      onClearDayPastDue,
      onSetAgeGroup,
      onClearAgeGroup,
    } = usePayableFeesFilters({ schoolId, filters, onSetFilters, schoolYears });

    const filterStyle = !schoolYear ? { opacity: 0.5 } : undefined;

    useImperativeHandle(ref, () => ({
      openArrangeBy: () => arrangeByDropdown.current?.open(),
    }));

    const handleSaveFilter = useCallback(() => {
      personalFiltersDropdown.current?.saveFilter();
    }, []);

    return (
      <FiltersContainer onOpenFilters={() => sidebar.current?.open()}>
        <PersonalFiltersDropdown
          ref={personalFiltersDropdown}
          currentUser={currentStaff}
          relationId={currentStaff?.relation_id || ''}
          onSaveFilter={handleSaveFilter}
          schoolId={schoolId}
          section={FilterSection.PayableFees}
          filters={{ ...filters, arrange_by: arrangeBy }}
          defaultSchoolYear={defaultSchoolYear}
          onSetFilters={(v) => {
            const filters = pickOnlyParamsFromFilterKeys(PAYABLE_FEES_QUERY_FILTER_KEYS, v);
            onSetFilters(filters);
            for (const key of PAYABLE_FEES_ARRANGE_BY_FILTER_KEYS) {
              key === v.arrange_by && onSetArrangeBy(key);
            }
          }}
        />

        {filtersDate && (
          <DateRangeDropdown
            schoolId={schoolId}
            date={filtersDate}
            onSetDate={onSetDate}
            openLabel={labels[FilterKeys.Date]}
            defaultSchoolYear={defaultSchoolYear}
            withinSingleSchoolYear
          />
        )}

        {!!(filtersProduct?.length || filtersProductTypes?.length) && (
          <FilterDropdown
            onClear={onClearProduct}
            label={labels[FilterKeys.Product]}
            tags={(open) => [
              ...(filtersProduct || []).map((id) => (
                <ProductTagSelect sx={{ maxWidth: 200 }} key={id} id={id} onClick={open} />
              )),
              ...(filtersProductTypes || []).map((id) => (
                <ProductTypeTagSelect
                  sx={{ maxWidth: 200 }}
                  key={id.toString()}
                  id={id}
                  onClick={open}
                />
              )),
            ]}
          >
            {(onClose) => (
              <ProductExpandedSelect
                schoolId={schoolId}
                selectedProductIds={filtersProduct || []}
                selectedProductTypeIds={filtersProductTypes || []}
                onApply={onSetProductAndProductType}
                schoolYear={schoolYear ?? undefined}
                onClose={onClose}
                onClear={onClearProduct}
              />
            )}
          </FilterDropdown>
        )}

        {!!filtersFrequencies?.length && (
          <FilterDropdown
            onClear={onClearFrequency}
            label={labels[FilterKeys.Frequency]}
            tags={(open) =>
              filtersFrequencies.map((type) => (
                <FrequencyTagSelect sx={{ maxWidth: 200 }} key={type} type={type} onClick={open} />
              ))
            }
          >
            {(onClose) => (
              <FrequencyExpandedSelect
                selectedTypes={filtersFrequencies}
                onApply={onSetFrequency}
                onClose={onClose}
                onClear={onClearFrequency}
              />
            )}
          </FilterDropdown>
        )}

        {!!filtersFeeStatus?.length && (
          <FilterDropdown
            onClear={onClearFeeStatus}
            label={labels[FilterKeys.FeeStatus]}
            tags={(open) =>
              filtersFeeStatus.map((status) => (
                <FeeStatusTagSelect
                  sx={{ maxWidth: 200 }}
                  key={status}
                  status={status}
                  onClick={open}
                />
              ))
            }
          >
            {(onClose) => (
              <FeeStatusExpandedSelect
                selectedStatuses={filtersFeeStatus}
                onApply={onSetFeeStatus}
                onClose={onClose}
                onClear={onClearFeeStatus}
              />
            )}
          </FilterDropdown>
        )}

        {!!filtersDayPastDue?.length && (
          <FilterDropdown
            onClear={onClearDayPastDue}
            label={labels[FilterKeys.DayPastDue]}
            tags={(open) =>
              filtersDayPastDue.map((type) => (
                <DayPastDueTagSelect sx={{ maxWidth: 200 }} key={type} type={type} onClick={open} />
              ))
            }
          >
            {(onClose) => (
              <DayPastDueExpandedSelect
                selectedTypes={filtersDayPastDue}
                onApply={onSetDayPastDue}
                onClose={onClose}
                onClear={onClearDayPastDue}
              />
            )}
          </FilterDropdown>
        )}

        {!!filtersAgeGroup?.length && (
          <FilterDropdown
            onClear={onClearAgeGroup}
            label={labels[FilterKeys.InvoicesAgeGroup]}
            tags={(open) =>
              renderPropertyGroupTags({
                selectedItems: selectedItemsForAgeGroups,
                onClick: open,
                getProperty: (i) =>
                  i.isGroup
                    ? getSchoolLevelById(i.id)
                    : { ...getAgeGroupById(i.id), type: SchoolPropertyType.AgeGroup },
                getTooltip: (i) =>
                  i.isGroup
                    ? getAgeGroupsByLevelId(i.id).map((ageGroup) => (
                        <Typography key={ageGroup.id}>{ageGroup.name}</Typography>
                      ))
                    : null,
                tagProps: {
                  userRole: SchoolUserRole.Student,
                  sx: { maxWidth: 200 },
                },
              })
            }
          >
            {(onClose) => (
              <AgeGroupExpandedSelect
                userRole={SchoolUserRole.Student}
                schoolId={schoolId}
                selectedIds={filtersAgeGroup}
                onApply={onSetAgeGroup}
                onClose={onClose}
                onClear={onClearAgeGroup}
              />
            )}
          </FilterDropdown>
        )}

        <Box sx={filterStyle}>
          <ArrangeByDropdown
            data-test-id={'filter-arrange-by'}
            ref={(r: any) => (arrangeByDropdown.current = r as ArrangeByDropdown)}
            onSelectOption={onSetArrangeBy}
            selectedOption={arrangeBy || undefined}
            options={PAYABLE_FEES_ARRANGE_BY_FILTER_KEYS}
            disabled={!schoolYear}
          />
        </Box>

        <PayableFeesFiltersSidebar
          ref={sidebar}
          defaultSchoolYear={defaultSchoolYear}
          defaultFilters={defaultFilters}
          filters={filters}
          onApply={(v) => {
            onSetFilters(v.filters);
            onSetArrangeBy(v.arrangeBy);
          }}
          schoolId={schoolId}
          arrangeBy={arrangeBy}
          schoolYears={schoolYears}
        />
      </FiltersContainer>
    );
  },
);
